import type DatePickerProperties from "./DatePicker.properties.ts";
import styles from "./desktop/DatePicker.desktop.module.css";
import { resolveDate } from "../../tools/converters/DateConverter.ts";
import { useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";

export const useDatePickerController = (props: DatePickerProperties) => {
  const [isOpen, setIsOpen] = useState(props.open);
  const [selectedDate, setSelectedDate] = useState(
    props.value ? new Date(props.value) : null,
  );
  const datePickerRef = useRef<DatePicker>(null);
  const lastDayOfMonth = (year: number, monthIndex: number) =>
    new Date(year, monthIndex + 1, 0);

  const nextSixMonthEnds = useMemo(() => {
    const now = new Date();
    return [1, 2, 3, 4, 5, 6].map((offset) => {
      const monthStart = new Date(
        now.getFullYear(),
        now.getMonth() + offset,
        1,
      );
      const end = lastDayOfMonth(
        monthStart.getFullYear(),
        monthStart.getMonth(),
      );
      const label = monthStart.toLocaleString(undefined, {
        month: "numeric",
        year: "numeric",
      });
      return { label, date: end };
    });
  }, []);

  const resolveCssColorClassByDates = (date: Date) => {
    if (
      !resolveDate(props.switchToRedAfterXDays) &&
      !resolveDate(props.daysBeforeSwitchToRed)
    ) {
      return "";
    }

    const now = new Date();
    if (
      resolveDate(props.daysBeforeSwitchToRed) &&
      date > now &&
      date < resolveDate(props.daysBeforeSwitchToRed)
    ) {
      return styles.afterWarnDate;
    }

    const greenArea =
      (props.switchToRedAfterXDays ?? 0) + (props.daysBeforeSwitchToRed ?? 0);
    const greenAreaDate = new Date(now);
    greenAreaDate.setDate(greenAreaDate.getDate() + greenArea);

    if (greenAreaDate < date) {
      return styles.afterMaxDate;
    }
  };

  const onCalendarButtonClick = (date: Date) => {
    setSelectedDate(date);
    setIsOpen(false);
    datePickerRef.current?.setOpen(false);

    if (props.onChange) {
      props.onChange(date);
    }
  };

  const onCalendarChange = (date: Date | null) => {
    setSelectedDate(date);

    if (props.onChange) {
      props.onChange(date);
    }
  };

  return {
    resolveCssColorClassByDates,
    onCalendarButtonClick,
    onCalendarChange,
    setIsOpen,
    nextSixMonthEnds,
    selectedDate,
    isOpen,
    datePickerRef,
  };
};
